/**
 * Normalizes a `selectByFeature` answer `value` into the canonical array shape
 * `[{ entityType, entityId, value }]`.
 *
 * Legacy data (written by mobile and the web `/logs` submit path) can persist
 * the answer as a flat object-map keyed by entityId: `{ [entityId]: [actions] }`
 * (in bike mode, keys are trail ids and `featureIds` is empty). Calling array
 * methods such as `.find`/`.every`/`.map` on that object throws a TypeError and
 * crashes the whole park-log form on render, so we convert it up-front and use
 * the result everywhere. When `value` is already an array (the common case for
 * freshly created/edited logs) it is returned unchanged so behavior is identical.
 *
 * This lives in its own dependency-free module so it can be unit tested without
 * pulling in the component's UI/import chain.
 *
 * @param {Array|Object|null|undefined} value the stored answer value
 * @param {Object} [entityIds]
 * @param {Array<String>} [entityIds.featureIds]
 * @param {Array<String>} [entityIds.trailIds]
 * @param {Array<String>} [entityIds.spatialFeatureIds]
 * @returns {Array<{entityType: String, entityId: String, value: Array}>}
 */
export function normalizeFeatureActionValue(value, {
  featureIds = [],
  trailIds = [],
  spatialFeatureIds = [],
} = {}) {
  // Already the canonical array shape — return unchanged (same reference).
  if (Array.isArray(value)) {
    return value;
  }
  // Legacy flat object-map keyed by entityId → resolve entityType by membership.
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([entityId, actions]) => {
      let entityType = 'FEATURE';
      if (trailIds.includes(entityId)) {
        entityType = 'TRAIL';
      } else if (spatialFeatureIds.includes(entityId)) {
        entityType = 'SPATIAL_FEATURE';
      }
      return {
        entityType,
        entityId,
        value: actions,
      };
    });
  }
  // null / undefined / string / other primitives — nothing selectable.
  return [];
}
